import {
  Deposit,
  Condition,
  ConditionType,
  DepositPayable,
  DepositType,
  FormCondition,
  useStaffGetSpecialConditions,
} from '@urbanx/agx.campaigns.client';
import { AgxDropdown, AgxColumn, AgxDatePicker } from '@urbanx/agx.ui-components2';
import { useSelectedContract } from 'hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormConditionDateOptions } from './useFormConditionDateOptions';
import { useSpecialConditionsOptions } from './useSpecialConditionsOptions';
import { toDateOnlyString } from '@urbanx/agx.ui-components2';

interface FormConditionOnDateProps {
  id: string;
  defaultValue?: FormCondition | Deposit;
  onSpecificDateOptions: Condition[];
  onValueChange: (_condition: FormCondition | Deposit) => void;
  conditionTypeOverride?: ConditionType;
  depositType?: DepositType;
}

export function FormConditionOnDate({
  id,
  defaultValue,
  onSpecificDateOptions,
  onValueChange,
  conditionTypeOverride,
  depositType,
}: FormConditionOnDateProps) {
  const { contract, campaignId } = useSelectedContract();
  const [value, setValue] = useState<(FormCondition | Deposit) | undefined>(defaultValue);
  const specificDateOptions = useFormConditionDateOptions(onSpecificDateOptions, true, depositType);

  const [specificDateCondition, setSpecificDateCondition] = useState<Condition | undefined>(
    () => defaultValue?.condition as Condition
  );

  const { data: specialConditions } = useStaffGetSpecialConditions(
    campaignId ?? '',
    contract?.contractId || ''
  );

  useEffect(() => {
    setValue(
      (prev) =>
        ({
          ...prev,
          condition: (specificDateCondition ?? undefined) as Condition & DepositPayable & Condition,
          ...(conditionTypeOverride && {
            conditionType: conditionTypeOverride,
          }),
          ...(specificDateCondition === Condition.OnDate &&
            !conditionTypeOverride && {
              conditionType:
                depositType === DepositType.Balance
                  ? ConditionType.Unconditional
                  : ConditionType.Contract,
            }),
          ...(specificDateCondition !== Condition.OnDate &&
            !conditionTypeOverride &&
            depositType === DepositType.Balance && {
              // NdaysFromDate also follows the derived unconditional date for balance deposits.
              conditionType:
                specificDateCondition === Condition.NdaysFromDate
                  ? ConditionType.Unconditional
                  : ConditionType.Contract,
            }),
          ...(specificDateCondition === Condition.OnSpecificDate && {
            depositPayable: depositType ? DepositPayable.On : undefined,
            daysFromDate: undefined,
            dayType: undefined,
            fromDateTimeUnit: undefined,
            specialConditionId: undefined,
            other: undefined,
          }),
        }) as FormCondition | Deposit
    );
  }, [specificDateCondition, conditionTypeOverride, depositType]);

  useEffect(() => {
    // Only update if the current condition is not in the available options
    const isCurrentConditionAvailable = specificDateOptions.find(
      (option) => option.value === specificDateCondition
    );

    if (isCurrentConditionAvailable || !specificDateCondition) return;

    // If current condition is not available, select the first valid option
    const updateTo = specificDateOptions.find((option) => option.value !== '')?.value as Condition;

    if (updateTo && updateTo !== specificDateCondition) {
      setSpecificDateCondition(updateTo);
    }
  }, [specificDateOptions, specificDateCondition]);

  useEffect(() => {
    if (value?.condition) {
      onValueChange(value);
    }
  }, [value]);

  const specialConditionsOptions = useSpecialConditionsOptions(
    specialConditions?.renderedConditions ?? []
  );

  const selectedSpecialCondition = useMemo(() => {
    if (!value) return '';

    return value?.specialConditionId || '';
  }, [value]);

  const onSelectedSpecialConditionChange = useCallback(
    (value: string) => {
      const foundId = value === '' ? undefined : value;

      setValue((prev) => ({
        ...prev,
        specialConditionId: foundId,
      }));
    },
    [specialConditions]
  );

  const financeSpecificDateDefaultValue = useMemo(() => {
    return value?.referenceDate ?? '';
  }, [value?.referenceDate]);

  return (
    <AgxColumn gap="spacing-lg" fill>
      <AgxDropdown
        id={`${id}-ConditionFromOptions`}
        options={specificDateOptions}
        selected={specificDateCondition ?? ''}
        onChange={(value) => {
          setSpecificDateCondition(value as Condition);
        }}
        hideOptionalText
        noHeader
      />

      {specificDateCondition === Condition.OnSpecificDate && (
        <AgxDatePicker
          id={`${id}-FinanceOnSpecificDate`}
          value={financeSpecificDateDefaultValue ?? undefined}
          noHeader
          variant="compact"
          stretch
          onChange={(d: Date | null) => {
            setValue((prev) => ({
              ...prev,
              referenceDate: toDateOnlyString(d),
            }));
          }}
          optionalText=""
        />
      )}

      {specificDateCondition === Condition.UponSatisfactionOfSpecialCondition && (
        <AgxDropdown
          id={`${id}-SpecialConditionDropdown`}
          options={specialConditionsOptions}
          selected={selectedSpecialCondition}
          onChange={onSelectedSpecialConditionChange}
          hideOptionalText
          required
        />
      )}
    </AgxColumn>
  );
}
